import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Phone, PhoneCall, ChevronDown, ChevronUp, Plus, GripVertical, Users2, Check, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';

import { userStorage } from '../utils/userStorage';
const API      = process.env.REACT_APP_BACKEND_URL;
const LIST_KEY = 'burningteam_warteliste';
const PHONE_KEY= 'burningteam_contact_phones';

const loadLocal = (key, fb) => { try { return JSON.parse(userStorage.getItem(key)) ?? fb; } catch { return fb; } };
const saveLocal = (key, v)  => userStorage.setItem(key, JSON.stringify(v));

const RANK_COLORS = ['bg-blue-600','bg-indigo-600','bg-violet-600','bg-purple-600','bg-fuchsia-500','bg-pink-500','bg-rose-500','bg-red-500','bg-orange-500','bg-amber-500'];
const rankColor   = (i) => RANK_COLORS[i] ?? 'bg-slate-500';

/* Kontakt-Feld Formatter */
const fmtDate = (iso) => { if (!iso) return null; try { return new Date(iso).toLocaleDateString('de-DE', { day:'2-digit', month:'2-digit', year:'numeric' }); } catch { return iso; } };
const fmtDatetime = (iso) => { if (!iso) return null; try { return new Date(iso).toLocaleString('de-DE', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }); } catch { return iso; } };

/* ═══════════════════════════════════════════════════════════ */
export default function Warteliste({ onClose }) {
  const navigate = useNavigate();

  const [items,    setItems]    = useState([]);
  const [phones,   setPhones]   = useState({});
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState({});

  const [showAdd,  setShowAdd]  = useState(false);
  const [newEntry, setNewEntry] = useState({ name: '', phone: '', note: '' });

  const [editPhoneId,  setEditPhoneId]  = useState(null);
  const [editPhoneVal, setEditPhoneVal] = useState('');

  /* X-Button confirm modal */
  const [confirmXId, setConfirmXId] = useState(null);

  /* drag refs */
  const draggingIdx = useRef(null);
  const dragOverIdx = useRef(null);
  const isDragging  = useRef(false);
  const listRef     = useRef(null);
  const [dragFrom,  setDragFrom] = useState(-1);
  const [dragOver,  setDragOver] = useState(-1);

  /* ─── Load ──────────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      setLoading(true);
      const savedPhones = loadLocal(PHONE_KEY, {});
      setPhones(savedPhones);
      const saved = loadLocal(LIST_KEY, []);

      try {
        const res = await axios.get(`${API}/api/teampartner`);
        const kk  = (res.data || []).filter(tp => tp.typ === 'kaltkontakt' && !tp.kontaktiert && !tp.pausiert);
        const savedIds = new Set(saved.map(s => s.id));
        const dbIds    = new Set(kk.map(k => k.id));
        const newFromDB = kk
          .filter(k => !savedIds.has(k.id))
          .map(k => ({
            id:         k.id,
            name:       k.name || '–',
            typ:        'kaltkontakt',
            contactId:  k.id,
            note:       k.notizen || '',
            phone:      savedPhones[k.id] || '',
            /* full contact data for expanded view */
            _contact:   k,
          }));
        const kept = saved.filter(s => s.typ === 'manual' || dbIds.has(s.contactId || s.id));
        /* re-enrich kept kaltkontakte with fresh DB data */
        const dbMap = Object.fromEntries(kk.map(k => [k.id, k]));
        const enriched = kept.map(s => s.typ === 'kaltkontakt' && dbMap[s.contactId]
          ? { ...s, _contact: dbMap[s.contactId] }
          : s);
        setItems([...enriched, ...newFromDB]);
      } catch {
        setItems(saved);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!loading) {
      /* strip _contact before storing (re-fetched on load) */
      saveLocal(LIST_KEY, items.map(({ _contact, ...rest }) => rest));
    }
  }, [items, loading]);

  const savePhones = (u) => { setPhones(u); saveLocal(PHONE_KEY, u); };
  const getPhone   = (item) => phones[item.id] || item.phone || '';

  /* ─── Actions ───────────────────────────────────────────── */
  const markJa = (id) => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    /* Aus Warteliste lokal entfernen + SOFORT persistieren (onClose unmountet vor useEffect-Flush) */
    const newItems = items.filter(i => i.id !== id);
    setItems(newItems);
    saveLocal(LIST_KEY, newItems.map(({ _contact, ...rest }) => rest));
    onClose();
    if (item.typ === 'manual') {
      /* Manuell → neuer Kontakt, Formular vorausfüllen (inkl. Notiz) */
      navigate('/kunden', {
        state: {
          openNewKontakt: true,
          prefill: {
            name: item.name,
            phone: getPhone(item),
            note: item.note || '',
          },
        },
      });
    } else {
      /* DB-Kaltkontakt → bestehenden Kontakt bearbeiten, kontaktiert=true vorsetzen */
      navigate('/kunden', {
        state: {
          openEditKontakt: true,
          editId: item.id,
          prefillKontaktiert: true,
        },
      });
    }
  };
  const markNein = (id) => setItems(prev => {
    const idx = prev.findIndex(i => i.id === id);
    if (idx === -1) return prev;
    return [...prev.slice(0, idx), ...prev.slice(idx + 1), prev[idx]];
  });

  /* X-Button: Später erneut versuchen (nach unten) */
  const handleRetryLater = (id) => {
    markNein(id);
    setConfirmXId(null);
  };

  /* X-Button: Aus Liste entfernen → DB-Kaltkontakt wird PAUSIERT, manual wird lokal gelöscht */
  const handleRemoveFromList = async (id) => {
    const item = items.find(i => i.id === id);
    if (!item) { setConfirmXId(null); return; }

    if (item.typ === 'kaltkontakt') {
      try {
        await axios.put(`${API}/api/teampartner/${item.id}`, {
          pausiert: true,
          pausiert_datum: new Date().toISOString(),
        });
        toast('Kontakt wurde pausiert');
      } catch (err) {
        toast.error('Fehler beim Pausieren');
        setConfirmXId(null);
        return;
      }
    } else {
      toast('Aus Anrufliste entfernt');
    }
    setItems(prev => prev.filter(i => i.id !== id));
    setConfirmXId(null);
  };

  const handleAddManual = () => {
    if (!newEntry.name.trim()) return;
    const id    = `manual-${Date.now()}`;
    const phone = newEntry.phone.trim();
    const upd   = { ...phones };
    if (phone) upd[id] = phone;
    savePhones(upd);
    setItems(prev => [...prev, { id, name: newEntry.name.trim(), typ: 'manual', note: newEntry.note || '', phone }]);
    setNewEntry({ name: '', phone: '', note: '' });
    setShowAdd(false);
    userStorage.setItem('bt_phone_notice_shown', '1');
  };

  const savePhone = (id, val) => {
    savePhones({ ...phones, [id]: val.trim() });
    setEditPhoneId(null);
    userStorage.setItem('bt_phone_notice_shown', '1');
  };

  const handlePhoneBook = async () => {
    if ('contacts' in navigator && 'ContactsManager' in window) {
      try {
        const contacts = await navigator.contacts.select(['name', 'tel'], { multiple: true });
        if (!contacts.length) return;
        const newItems  = contacts.map(c => ({
          id:    `manual-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          name:  c.name?.[0] || 'Unbekannt',
          typ:   'manual',
          phone: c.tel?.[0] || '',
          note:  '',
        }));
        const updPhones = { ...phones };
        newItems.forEach(i => { if (i.phone) updPhones[i.id] = i.phone; });
        savePhones(updPhones);
        setItems(prev => [...prev, ...newItems]);
        userStorage.setItem('bt_phone_notice_shown', '1');
        toast(`${newItems.length} Kontakt${newItems.length !== 1 ? 'e' : ''} hinzugefügt`);
      } catch { toast('Telefonbuch-Zugriff abgebrochen'); }
    } else {
      toast('Nur auf Chrome/Android verfügbar – kommt bald');
    }
  };

  /* ─── Drag helpers ──────────────────────────────────────── */
  const findOver = useCallback((clientY) => {
    if (!listRef.current) return 0;
    const nodes = listRef.current.querySelectorAll('[data-drag-item]');
    for (let i = 0; i < nodes.length; i++) {
      const r = nodes[i].getBoundingClientRect();
      if (clientY < r.top + r.height / 2) return i;
    }
    return Math.max(0, nodes.length - 1);
  }, []);

  /* global move + end – registered once (Touch + Mouse) */
  useEffect(() => {
    const onTouchMove = (e) => {
      if (!isDragging.current) return;
      e.preventDefault();
      const over = findOver(e.touches[0].clientY);
      dragOverIdx.current = over;
      setDragOver(over);
    };
    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      e.preventDefault();
      const over = findOver(e.clientY);
      dragOverIdx.current = over;
      setDragOver(over);
    };
    const onEnd = () => {
      if (!isDragging.current) return;
      const from = draggingIdx.current;
      const to   = dragOverIdx.current;
      if (from !== null && to !== null && from !== to) {
        setItems(prev => {
          const arr = [...prev];
          const [moved] = arr.splice(from, 1);
          arr.splice(to, 0, moved);
          return arr;
        });
      }
      if (listRef.current) { listRef.current.style.overflowY = ''; listRef.current.style.touchAction = ''; }
      isDragging.current  = false;
      draggingIdx.current = null;
      dragOverIdx.current = null;
      setDragFrom(-1);
      setDragOver(-1);
    };
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend',  onEnd);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup',   onEnd);
    return () => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend',  onEnd);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup',   onEnd);
    };
  }, [findOver]);

  const handleDragStart = useCallback((e, index) => {
    e.stopPropagation();
    draggingIdx.current = index;
    dragOverIdx.current = index;
    isDragging.current  = true;
    if (listRef.current) { listRef.current.style.overflowY = 'hidden'; listRef.current.style.touchAction = 'none'; }
    if (navigator.vibrate) navigator.vibrate(40);
    setDragFrom(index);
    setDragOver(index);
  }, []);

  /* ─── Expanded contact fields ───────────────────────────── */
  const ContactDetail = ({ item }) => {
    const c     = item._contact || {};
    const phone = getPhone(item);
    const rows  = [];

    // Telefon (lokal)
    rows.push({ label: 'Telefon', content: (
      editPhoneId === item.id ? (
        <div className="flex gap-1.5 flex-1">
          <input autoFocus type="tel" value={editPhoneVal}
            onChange={e => setEditPhoneVal(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && savePhone(item.id, editPhoneVal)}
            className="flex-1 border border-slate-200 rounded-lg px-2 py-1 text-xs min-w-0 focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Nummer eingeben..." />
          <button type="button" onClick={() => savePhone(item.id, editPhoneVal)} className="px-2 py-1 bg-emerald-500 text-white rounded-lg text-xs font-semibold">OK</button>
          <button type="button" onClick={() => setEditPhoneId(null)} className="px-2 py-1 bg-slate-200 rounded-lg text-xs">✕</button>
        </div>
      ) : (
        <button type="button" onClick={() => { setEditPhoneId(item.id); setEditPhoneVal(phone); }}
          className={`text-xs underline ${phone ? 'text-blue-600 font-medium' : 'text-slate-400'}`}>
          {phone || 'Tippen zum Hinzufügen'}
        </button>
      )
    )});

    if (c.naechster_termin) rows.push({ label: 'Nächster Termin', value: fmtDatetime(c.naechster_termin) });
    if (c.naechstes_thema)  rows.push({ label: 'Nächstes Thema',  value: c.naechstes_thema });
    if (c.letzter_termin)   rows.push({ label: 'Zuletzt kontaktiert', value: fmtDatetime(c.letzter_termin) });
    if (c.thematik?.length) rows.push({ label: 'Thematik', value: c.thematik.join(', ') });
    const notiz = c.notizen || item.note;
    if (notiz)              rows.push({ label: 'Notiz', value: notiz });
    if (c.actionpack && c.actionpack !== 'nein') rows.push({ label: 'ActionPack', value: c.actionpack === 'ja' ? 'Ja' : 'Interesse' });
    if (c.stufe && c.stufe !== 'keine') rows.push({ label: 'Stufe', value: c.stufe });
    if (c.im_abo?.length)   rows.push({ label: 'Im Abo', value: c.im_abo.join(', ') });
    if (c.created_at)       rows.push({ label: 'Angelegt am', value: fmtDate(c.created_at) });

    return (
      <div className="px-4 pb-4 pt-2 bg-slate-50 border-t border-slate-100 space-y-2">
        {!userStorage.getItem('bt_phone_notice_shown') && (
          <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mb-1">
            📱 Telefonnummer wird <strong>nur lokal</strong> auf diesem Gerät gespeichert.
          </p>
        )}
        {rows.map((row, i) => (
          <div key={i} className="flex gap-2 items-start">
            <span className="text-[11px] font-semibold text-slate-500 w-32 flex-shrink-0 pt-0.5">{row.label}</span>
            {row.content ?? <span className="text-xs text-slate-700 flex-1">{row.value}</span>}
          </div>
        ))}
        {rows.length === 1 && item.typ === 'kaltkontakt' && (
          <p className="text-[10px] text-slate-400 italic">Keine weiteren Daten eingetragen</p>
        )}
        {item.typ === 'kaltkontakt' && (
          <p className="text-[10px] text-slate-400 mt-1">Quelle: Neue Kontakte (Datenbank)</p>
        )}
      </div>
    );
  };

  /* ─── Render ────────────────────────────────────────────── */
  /* global no-select style while dragging */
  const noSelect = { userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' };

  return (
    <div className="fixed inset-0 z-[300] flex flex-col" style={{ background: '#f1f5f9' }}>

      {/* Header */}
      <div className="flex-shrink-0 bg-gradient-to-r from-blue-600 to-indigo-700 px-4 py-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-xl tracking-tight">Anrufliste</h2>
            <p className="text-blue-200 text-xs mt-0.5">{items.length} Kontakte · ≡ ziehen zum Sortieren</p>
          </div>
          <button type="button" onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Action row */}
      <div className="flex-shrink-0 px-4 py-2.5 bg-white border-b border-slate-200 flex gap-2 shadow-sm">
        <button type="button" onClick={() => setShowAdd(true)}
          className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors shadow-sm">
          <Plus className="w-4 h-4" /> Hinzufügen
        </button>
        <button type="button" onClick={handlePhoneBook}
          className="flex-1 py-2.5 bg-white border border-dashed border-slate-300 hover:bg-slate-50 text-slate-500 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors">
          <Users2 className="w-4 h-4" /> Aus Telefonbuch
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-3 space-y-2" ref={listRef}
        style={{ touchAction: 'pan-y' }}>

        {loading && <div className="flex items-center justify-center h-40 text-slate-400 text-sm">Lade...</div>}

        {!loading && items.length === 0 && (
          <div className="flex flex-col items-center justify-center h-52 gap-3 text-slate-400">
            <PhoneCall className="w-14 h-14 text-slate-200" />
            <p className="text-sm font-medium">Noch keine Kontakte</p>
            <p className="text-xs text-slate-300">Tippe auf „Hinzufügen"</p>
          </div>
        )}

        {!loading && items.map((item, index) => {
          const phone    = getPhone(item);
          const isLifted = dragFrom === index;
          const isOver   = dragOver === index && dragFrom !== -1 && dragFrom !== index;
          const isExpd   = expanded[item.id];

          return (
            <div key={item.id} data-drag-item style={noSelect}
              className={`rounded-2xl overflow-hidden bg-white shadow-sm border transition-all duration-150
                ${isLifted ? 'opacity-25 scale-[0.97] shadow-none' : 'border-slate-200 shadow-sm'}
                ${isOver   ? 'border-t-[3px] border-t-blue-500' : ''}
              `}
            >
              {/* ── Compact row ── */}
              <div className="flex items-center gap-2 px-3 py-3">

                {/* Rank badge */}
                <span className={`w-6 h-6 rounded-full ${rankColor(index)} text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 shadow-sm`}>
                  {index + 1}
                </span>

                {/* Drag handle – touch-action:none prevents browser scroll capture */}
                <div
                  style={{ touchAction: 'none', cursor: 'grab', WebkitUserSelect: 'none', userSelect: 'none' }}
                  className="flex-shrink-0 p-1.5 -ml-1 rounded-lg active:bg-slate-100 active:cursor-grabbing hover:bg-slate-50"
                  onTouchStart={(e) => handleDragStart(e, index)}
                  onMouseDown={(e) => handleDragStart(e, index)}
                >
                  <GripVertical className="w-4 h-4 text-slate-300" />
                </div>

                {/* Name – flex-1 to fill space */}
                <span className="flex-1 text-sm font-semibold text-slate-800 min-w-0 leading-snug" style={{ display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                  {item.name}
                </span>

                {/* Call button */}
                {phone ? (
                  <a href={`tel:${phone}`} onClick={e => e.stopPropagation()}
                    className="w-8 h-8 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center flex-shrink-0 shadow-sm transition-colors">
                    <Phone className="w-3.5 h-3.5 text-white" />
                  </a>
                ) : (
                  <button type="button"
                    onClick={() => { setEditPhoneId(item.id); setEditPhoneVal(''); setExpanded(p => ({ ...p, [item.id]: true })); }}
                    className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0 transition-colors">
                    <Phone className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                )}

                {/* Ja – icon only */}
                <button type="button" onClick={() => markJa(item.id)}
                  title="Kontaktiert"
                  className="w-8 h-8 rounded-full bg-emerald-100 hover:bg-emerald-200 flex items-center justify-center flex-shrink-0 transition-colors border border-emerald-300">
                  <Check className="w-4 h-4 text-emerald-700 stroke-[2.5]" />
                </button>

                {/* Nein – icon only → Modal öffnen */}
                <button type="button" onClick={() => setConfirmXId(item.id)}
                  title="Nicht kontaktiert"
                  className="w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 flex items-center justify-center flex-shrink-0 transition-colors border border-red-300">
                  <XIcon className="w-4 h-4 text-red-600 stroke-[2.5]" />
                </button>

                {/* Expand */}
                <button type="button"
                  onClick={() => setExpanded(p => ({ ...p, [item.id]: !p[item.id] }))}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0">
                  {isExpd ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </button>
              </div>

              {/* Expanded */}
              {isExpd && <ContactDetail item={item} />}
            </div>
          );
        })}

        <div className="h-4" />
      </div>

      {/* Add Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/60 flex items-end z-[400]" onClick={() => setShowAdd(false)}>
          <div className="bg-white w-full rounded-t-3xl px-5 pb-10 pt-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-5" />
            <h3 className="text-base font-bold text-slate-800 mb-1">Kontakt hinzufügen</h3>
            <p className="text-xs text-slate-500 mb-4">Name ist Pflicht · Telefon + Notiz optional</p>

            {!userStorage.getItem('bt_phone_notice_shown') && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-2xl px-3 py-2.5 mb-4">
                <span className="text-base leading-none flex-shrink-0">📱</span>
                <span className="text-xs text-amber-800">Telefonnummer wird <strong>nur lokal</strong> auf diesem Gerät gespeichert – nicht in der Cloud.</span>
              </div>
            )}

            <div className="space-y-3">
              <input value={newEntry.name} autoFocus
                onChange={e => setNewEntry(p => ({ ...p, name: e.target.value }))}
                className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Name *" />
              <input type="tel" value={newEntry.phone}
                onChange={e => setNewEntry(p => ({ ...p, phone: e.target.value }))}
                className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Telefonnummer (lokal gespeichert)" />
              <textarea value={newEntry.note} rows={2}
                onChange={e => setNewEntry(p => ({ ...p, note: e.target.value }))}
                className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Notiz (optional)" />
            </div>

            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setShowAdd(false)}
                className="flex-1 py-3 rounded-2xl border border-slate-200 text-sm text-slate-600 font-medium">
                Abbrechen
              </button>
              <button type="button" onClick={handleAddManual} disabled={!newEntry.name.trim()}
                className="flex-1 py-3 rounded-2xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white font-semibold text-sm transition-colors">
                Hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Confirm X Modal – "Später erneut versuchen" vs "Aus Liste entfernen" */}
      {confirmXId && (() => {
        const confirmItem = items.find(i => i.id === confirmXId);
        if (!confirmItem) { setConfirmXId(null); return null; }
        return (
          <div className="fixed inset-0 bg-black/60 flex items-end z-[400]" onClick={() => setConfirmXId(null)}>
            <div className="bg-white w-full rounded-t-3xl px-5 pb-10 pt-5 shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-5" />
              <h3 className="text-base font-bold text-slate-800 mb-1" data-testid="warteliste-confirm-x-title">
                {confirmItem.name}
              </h3>
              <p className="text-sm text-slate-500 mb-5">Was möchtest du mit diesem Kontakt machen?</p>

              <div className="space-y-2">
                <button type="button"
                  data-testid="warteliste-retry-later-btn"
                  onClick={() => handleRetryLater(confirmItem.id)}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-amber-50 hover:bg-amber-100 border border-amber-200 transition-colors text-left">
                  <span className="text-xl flex-shrink-0">⏳</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-900">Später erneut versuchen</p>
                    <p className="text-[11px] text-amber-700 mt-0.5">Wird ans Ende der Liste verschoben</p>
                  </div>
                </button>

                <button type="button"
                  data-testid="warteliste-remove-btn"
                  onClick={() => handleRemoveFromList(confirmItem.id)}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-red-50 hover:bg-red-100 border border-red-200 transition-colors text-left">
                  <span className="text-xl flex-shrink-0">⏸️</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-red-800">
                      {confirmItem.typ === 'kaltkontakt' ? 'Kontakt pausieren' : 'Aus Liste entfernen'}
                    </p>
                    <p className="text-[11px] text-red-600 mt-0.5">
                      {confirmItem.typ === 'kaltkontakt'
                        ? 'Kontakt wird auf „Pausiert" gesetzt. Hebst du die Pausierung später auf, erscheint er wieder hier.'
                        : 'Manueller Eintrag wird gelöscht'}
                    </p>
                  </div>
                </button>
              </div>

              <button type="button" onClick={() => setConfirmXId(null)}
                data-testid="warteliste-confirm-cancel-btn"
                className="w-full mt-4 py-3 rounded-2xl border border-slate-200 text-sm text-slate-600 font-medium">
                Abbrechen
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
